import { ShieldCheck, Sparkles, HeartPulse } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

const SPOTS = [
  {
    key: "sterilization",
    label: "Sterilisation Room + Lab",
    image: "/clinic/sterilization.jpg",
    icon: ShieldCheck,
    accent: "#2D4A3E",
    headline: "Autoclave-tested for every single patient.",
    body:
      "A dedicated sterilisation room and in-house lab keep every instrument, tray and impression as clean as the day it was unboxed. Because painless dentistry only counts when it's also safe dentistry.",
    pill: "Hospital-grade hygiene",
  },
  {
    key: "treatment",
    label: "Treatment Suite",
    image: "/clinic/treatment-room.jpg",
    icon: HeartPulse,
    accent: "#D4B27E",
    headline: "A calm, modern chair — the kind you actually don't mind sitting in.",
    body:
      "Digital X-ray, LED overhead lighting, chair-side monitors and a fully-air-conditioned suite. Every treatment room is designed so we can explain your care as it happens, and so you can breathe easy through it.",
    pill: "Modern equipment",
  },
];

export default function ClinicTour() {
  const [headRef, headVis] = useReveal();

  return (
    <section
      id="clinic-tour"
      data-testid="section-clinic-tour"
      className="relative py-24 md:py-32 bg-[#FAFAF9] overflow-hidden"
    >
      <div aria-hidden className="absolute -top-32 right-0 w-[520px] h-[520px] rounded-full bg-[#2D4A3E]/[0.06] blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[#D4B27E]/15 blur-3xl float-slow" />

      <div className="container-x relative">
        <div
          ref={headRef}
          className={`max-w-3xl reveal ${headVis ? "is-visible" : ""}`}
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#2D4A3E] font-medium">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} /> Take a look inside
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1C221F] tracking-tight leading-[1.05]">
            Modern, spotless,{" "}
            <span className="shine">and made for you.</span>
          </h2>
          <p className="mt-5 text-[#57605B] leading-relaxed">
            No dark corridors. No lingering antiseptic smell. Just a bright, welcoming space
            built around your comfort — where every corner has been thought through, and every
            instrument has been sterilised.
          </p>
        </div>

        <div className="mt-12 md:mt-16 grid md:grid-cols-2 gap-6 md:gap-8">
          {SPOTS.map((s, i) => (
            <TourCard key={s.key} spot={s} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function TourCard({ spot, idx }) {
  const [ref, visible] = useReveal();
  const Icon = spot.icon;
  return (
    <article
      ref={ref}
      data-testid={`clinic-tour-${spot.key}`}
      style={{ transitionDelay: visible ? `${idx * 120}ms` : "0ms" }}
      className={`group relative overflow-hidden rounded-[2rem] border border-stone-200 bg-white soft-shadow reveal ${
        visible ? "is-visible" : ""
      }`}
    >
      <div className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden">
        <img
          src={spot.image}
          alt={spot.label}
          loading="lazy"
          className="w-full h-full object-cover object-center transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0e1512]/85 via-[#0e1512]/20 to-transparent" />

        {/* Top pill */}
        <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 border border-stone-200">
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} style={{ color: spot.accent }} />
          <span className="text-[11px] font-medium text-[#1C221F] tracking-wide">
            {spot.label}
          </span>
        </div>

        {/* Bottom copy */}
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-white">
          <div className="text-[10px] uppercase tracking-[0.25em] font-medium" style={{ color: "#D4B27E" }}>
            {spot.pill}
          </div>
          <h3 className="mt-2 font-display text-xl sm:text-2xl md:text-3xl font-semibold leading-tight tracking-tight max-w-md">
            {spot.headline}
          </h3>
          <p className="mt-3 text-sm text-white/80 leading-relaxed max-w-md">
            {spot.body}
          </p>
        </div>
      </div>
    </article>
  );
}
